import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface NotificacionPendiente {
  id_notificacion: string;
  tipo: string | null;
  titulo: string;
  mensaje: string;
  leido: boolean;
  fecha_creacion: string;
  // Cita a la que apunta la notificación (null en tipos que no son de cita).
  // Permiten que la campana lleve al día exacto del calendario.
  id_cita: string | null;
  fecha_cita: string | null;
}

/**
 * Notificaciones pendientes (no leídas) del usuario logueado, acotadas al
 * PERFIL Y EMPRESA ACTIVOS en este momento (localStorage: id_perfil,
 * id_empresa). Un mismo usuario puede tener perfil de empresa y de
 * trabajador (incluso en empresas distintas); sin este filtro se verían
 * mezcladas las notificaciones de ambos paneles, lo cual no tiene sentido
 * para quien las recibe.
 *
 * Lee de vista_notificaciones_pendientes (ya filtra leido = false).
 * Se mantiene al día en tiempo real vía Realtime sobre la tabla
 * `notificaciones` (ya está en la publicación supabase_realtime).
 */
export function useNotificacionesPendientes() {
  const [notificaciones, setNotificaciones] = useState<NotificacionPendiente[]>([]);
  const [loading, setLoading] = useState(true);
  const userIdRef = useRef<string | null>(null);

  const cargar = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setNotificaciones([]);
      setLoading(false);
      return;
    }

    userIdRef.current = user.id;

    const idPerfil = localStorage.getItem('id_perfil');
    const idEmpresa = localStorage.getItem('id_empresa');

    let query = supabase
      .from('vista_notificaciones_pendientes')
      .select(
        'id_notificacion, tipo, titulo, mensaje, leido, fecha_creacion, id_cita, fecha_cita'
      )
      .eq('id_usuario', user.id);

    if (idPerfil) {
      query = query.eq('id_perfil', idPerfil);
    }
    if (idEmpresa) {
      query = query.eq('id_empresa', idEmpresa);
    }

    const { data, error } = await query.order('fecha_creacion', { ascending: false });

    if (error) {
      console.error('useNotificacionesPendientes (cargar):', error);
    } else {
      setNotificaciones((data ?? []) as NotificacionPendiente[]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await cargar();
    })();
  }, [cargar]);

  // Realtime: cualquier INSERT/UPDATE/DELETE en notificaciones de este
  // usuario recarga la lista (más simple y fiable que parchear el payload
  // a mano, y el volumen de notificaciones por usuario es bajo).
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      channel = supabase
        .channel(`notificaciones-usuario-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notificaciones',
            filter: `id_usuario=eq.${user.id}`,
          },
          () => {
            cargar();
          }
        )
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [cargar]);

  const marcarLeida = useCallback(async (id_notificacion: string) => {
    const anteriores = notificaciones;
    setNotificaciones((prev) => prev.filter((n) => n.id_notificacion !== id_notificacion));

    const { error } = await supabase
      .from('notificaciones')
      .update({ leido: true })
      .eq('id_notificacion', id_notificacion);

    if (error) {
      console.error('useNotificacionesPendientes (marcarLeida):', error);
      setNotificaciones(anteriores);
    }
  }, [notificaciones]);

  const marcarTodasLeidas = useCallback(async () => {
    if (!userIdRef.current) return;
    const anteriores = notificaciones;
    setNotificaciones([]);

    const { error } = await supabase
      .from('notificaciones')
      .update({ leido: true })
      .eq('id_usuario', userIdRef.current)
      .eq('leido', false);

    if (error) {
      console.error('useNotificacionesPendientes (marcarTodasLeidas):', error);
      setNotificaciones(anteriores);
    }
  }, [notificaciones]);

  return {
    notificaciones,
    total: notificaciones.length,
    loading,
    marcarLeida,
    marcarTodasLeidas,
    recargar: cargar,
  };
}